import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Menu, LogOut } from 'lucide-react';

import { PRIMARY_MODULES, ADMIN_MODULES, type ModuleDef } from '@/lib/modules';
import { useAuth } from '@/hooks/useAuth';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

export function MobileNavTrigger() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button className="md:hidden p-2 -ml-2 rounded-md hover:bg-muted transition-colors" aria-label="Open menu">
          <Menu className="w-5 h-5" />
        </button>
      </SheetTrigger>
      <SheetContent side="left" className="w-72 p-0 flex flex-col">
        <SheetTitle className="sr-only">KEJJA Navigation</SheetTitle>

        <div className="h-28 flex items-center justify-center border-b border-border px-4">
          <img src="/kejja.svg" alt="KEJJA" className="h-16 w-auto object-contain dark:hidden" />
          <img src="/kejja-white.svg" alt="KEJJA" className="h-16 w-auto object-contain hidden dark:block" />
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-4">
          <MobileNavSection items={PRIMARY_MODULES} onNavigate={() => setOpen(false)} />
          <Separator />
          <MobileNavSection items={ADMIN_MODULES} onNavigate={() => setOpen(false)} />
        </nav>

        <div className="p-3 border-t border-border">
          <div className="flex items-center gap-3 p-2 mb-2">
            <Avatar className="h-9 w-9">
              <AvatarImage src={user?.avatarUrl ?? undefined} />
              <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                {user?.displayName?.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase() || '?'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.displayName}</p>
              <p className="text-[11px] text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={() => { setOpen(false); logout(); }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground hover:bg-muted transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function MobileNavSection({ items, onNavigate }: { items: ModuleDef[]; onNavigate: () => void }) {
  return (
    <ul className="space-y-0.5">
      {items.map((m) => (
        <li key={m.key}>
          <NavLink
            to={m.route}
            end={m.route === '/dashboard'}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors',
                isActive
                  ? 'bg-primary/10 text-primary font-medium'
                  : 'text-foreground hover:bg-muted',
              )
            }
          >
            <m.icon className="w-4 h-4" />
            <span>{m.label}</span>
            {m.status === 'coming_soon' && (
              <span className="ml-auto text-[10px] uppercase tracking-wide text-muted-foreground">soon</span>
            )}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}
